<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('stock_management.predefined_lot_numbers.title')"
      :grid-ref="$refs[gridRefKey]"
      :show-add="canEdit"
      :show-filter="false"
      @add="handleAddClick"
    />
    <DxDataGrid
      key-expr="uuid"
      :ref="gridRefKey"
      :data-source="predefinedLotNumbersDataSource"
      :remote-operations="gridRemoteOperations"
      :column-min-width="80"
      style="min-height: 350px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :showBorders="true"
      :column-auto-width="false"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :element-attr="gridAttributes"
      @exporting="onExporting"
      @editing-start="onEditingStart"
      :row-height="50"
    >
      <DxColumn
        dataField="lot_number"
        :caption="$t('stock_management.predefined_lot_numbers.lot_number')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      />
      <DxColumn
        dataField="product_name"
        :caption="$t('stock_management.predefined_lot_numbers.product')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      />
      <DxColumn
        dataField="product_code"
        :caption="$t('stock_management.predefined_lot_numbers.product_code')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      />
      <DxColumn
        dataField="parcel_name"
        :caption="$t('stock_management.predefined_lot_numbers.parcel')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      />
      <DxColumn
        dataField="season_uuid"
        cell-template="seasonViewTemplate"
        :caption="$t('stock_management.predefined_lot_numbers.season')"
        :filter-operations="['=']"
        :allowSorting="false"
        :allowHeaderFiltering="false"
        :calculateCellValue="getSeasonColumnText"
      >
        <DxLookup :data-source="seasons" value-expr="season_uuid" display-expr="name" />
      </DxColumn>
      <template #seasonViewTemplate="{ data }">
        <span>{{ getSeasonColumnText(data.data.season_uuid) }}</span>
      </template>
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="() => true"
        />
      </template>
      <DxEditing mode="row" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="$t('common.confirm_delete', { title: $t('stock_management.predefined_lot_numbers.title') })"
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxSearchPanel :visible="false" />
      <DxGroupPanel :visible="false" />
      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'PredefinedLotNumbers'" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxSearchPanel,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxLookup,
} from "devextreme-vue/data-grid";
import Forbidden from "~/pages/errors/Forbidden.vue";
import CustomStore from "devextreme/data/custom_store";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import notify from "devextreme/ui/notify";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";

export default {
  name: "PredefinedLotNumbers",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxPaging,
    DxPager,
    DxSearchPanel,
    DxExport,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxLookup,
    Forbidden,
    GridPanelHeader,
    DxActionMenu,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid predefined-lot-numbers-grid",
      },
      gridRefKey: "predefinedLotNumbersGrid",
      predefinedLotNumbersDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = { data: [], totalCount: 0 };
          let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;
          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          };
          try {
            await this.getPredefinedLotNumbers(payload).then((response) => {
              dataSource.data = response.data.data;
              dataSource.totalCount = response.data?.pagination?.total ?? 1;
            });
            return dataSource;
          } catch (error) {
            console.error("Error fetching predefined lot numbers:", error);
            throw error;
          }
        },
        remove: this.removePredefinedLotNumber,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 25, 100],
      seasons: [],
    };
  },
  computed: {
    config() {
      return {
        permission: permissions.SM_PREDEFINED_LOT_NUMBER,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    dataGridInstance() {
      const grid = this.$refs && this.$refs[this.gridRefKey] ? this.$refs[this.gridRefKey] : null;
      return grid && grid.instance ? grid.instance : null;
    },
  },
  methods: {
    ...mapActions({
      getPredefinedLotNumbers: "smPredefinedLotNumbers/getPredefinedLotNumbers",
      deletePredefinedLotNumber: "smPredefinedLotNumbers/deletePredefinedLotNumber",
      getSeasons: "seasons/getSeasons",
    }),
    handleAddClick() {
      this.$router.push({ name: "predefined-lot-numbers-edit", params: { uuid: "add" } });
    },
    getSeasonColumnText(season_uuid) {
      const season = this.seasons.find((s) => s.season_uuid === season_uuid);
      return season ? season.name : "";
    },
    onEditingStart(e) {
      e.cancel = true;
      this.$router.push({ name: "predefined-lot-numbers-edit", params: { uuid: e.data.uuid } });
    },
    async removePredefinedLotNumber(key) {
      try {
        const response = await this.deletePredefinedLotNumber({ uuid: key });
        this.notifyMsg(response);
      } catch (error) {
        console.error("Error deleting predefined lot number:", error);
        throw error;
      }
    },
    onExporting(e) {
      e.fileName = "PredefinedLotNumbers_" + format(new Date(), "yyyy_MM_dd_HH_mm_ss");
      if (e.format === "xlsx") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("PredefinedLotNumbers");
        exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
        });
      }
    },
    notifyMsg(response) {
      notify({
        message: response.message ?? "",
        type: response.status === "ok" ? "success" : "error",
        displayTime: 2500,
        width: "auto",
        position: { at: "top center", my: "top center" },
      });
    },
  },
  mounted() {
    this.getSeasons({ limit: 1000 })
      .then((response) => {
        this.seasons = (response.data ?? []).map((season) => ({
          season_uuid: season.uuid,
          name: season.name,
          year: season.year,
        }));
      })
      .catch((error) => {
        console.error("Error fetching seasons:", error);
      });
  },
};
</script>
